import cloneDeep from 'lodash/cloneDeep'
import filter from 'lodash/filter'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { NativeModal, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { ErrorPromptModal } from '@Domains/Cart/Components/Prompt/Error/ErrorPrompt'
import { GiftCardAmountDrawer } from '@Domains/Cart/Components/Prompt/GiftCardAmount'
import { GiftCardTextDrawer } from '@Domains/Cart/Components/Prompt/GiftCardText/GiftCardTextPrompt'
import { removeAddToCartInput } from '@Domains/Cart/Components/Prompt/inputs'
import { SizeDrawer } from '@Domains/Cart/Components/Prompt/Size'
import { getSelectedPersonalizationType, PromptStep, reconcilePromptSteps } from '@Domains/Cart/Components/Prompt/Stepper/steps'
import { SuccessPrompt } from '@Domains/Cart/Components/Prompt/Success/SuccessPrompt'
import { TextPersonalizationPrompt } from '@Domains/Cart/Components/Prompt/TextPersonalization/Prompt'
import { Prompt, Prompts, PromptSelectVariant } from '@Domains/Cart/Components/Prompt/types'
import { AddToCartFuncSync } from '@Domains/Cart/Hooks/useAddToCart'
import { AddToCartProductInput, ProductCrossSellAndPersonalizationContentFragment } from '@Generated/graphql'
import { BubbleNavigation } from '@Shared/Components/Navigation/Buble/BubbleNavigation'

import styles from './Dialog.module.scss'

interface PromptInput {
  prompts: Prompts
  promptsOrigin: AddToCartProductInput[] | null
}

interface PromptDialogProps extends PromptInput {
  isLoading?: boolean
  onClose: () => void
  onSubmit: AddToCartFuncSync
  portal?: boolean
}

// Literal markers so key-scanning i18n tooling finds the keys used as data below:
// t('products:CHOOSE_SIZE') t('products:CHOOSE_ENGRAVING') t('products:CHOOSE_DEBOSSING')
const STEP_LABELS = {
  deboss: { fallback: 'Choose debossing', key: 'products:CHOOSE_DEBOSSING' },
  engrave: { fallback: 'Choose engraving', key: 'products:CHOOSE_ENGRAVING' },
  size: { fallback: 'Choose size', key: 'products:CHOOSE_SIZE' },
}

const isSelectVariantPrompt = (prompt: Prompt): prompt is PromptSelectVariant => prompt.__typename === 'AddToCartPromptSelectVariant'

export const PromptDialog: FC<PromptDialogProps> = ({
  onClose,
  onSubmit,
  portal,
  prompts,
  promptsOrigin = [],
  isLoading = false,
}) => {
  const modalActionsRef = useNativeModalActions()
  const [sourceID, setSourceIDPrompt] = useState<string | null>(null)
  const [enableBackdropClick, setEnableBackdropClick] = useState(true)
  const variantModalInAction = useRef(false)
  const activePrompt = prompts?.[0] || null
  const [selectVariantPrompts, setSelectVariantPrompts] = useState<PromptSelectVariant[]>([])
  const selectedPersonalizationType = activePrompt ? getSelectedPersonalizationType(activePrompt) : false

  // Held in a ref so completed steps keep showing after their prompts resolve.
  const activePromptIndex = activePrompt && 'index' in activePrompt ? activePrompt.index : -1
  const stepsRef = useRef<PromptStep[]>([])
  stepsRef.current = activePrompt ? reconcilePromptSteps(stepsRef.current, prompts, promptsOrigin, activePromptIndex) : []
  const steps = stepsRef.current
  const activeStepIndex = steps.findIndex(step => step.inputIndex === activePromptIndex)

  const stepLabel = (step: PromptStep) => {
    if (step.type === 'size') {
      return STEP_LABELS.size
    }
    return step.personalizationType === 'deboss' ? STEP_LABELS.deboss : STEP_LABELS.engrave
  }
  const navSteps = steps.map((step) => {
    const label = stepLabel(step)
    return { fallback: label.fallback, key: step.key, title: label.key }
  })

  // Grouped multi-size flows keep SizeDrawer's "(1/3)" counter; the accumulated
  // selectVariantPrompts check covers the group's last step, when prompts shrinks to one.
  const isGroupedSizeFlow = selectVariantPrompts.length > 1
    || (prompts.length > 1 && prompts.every(isSelectVariantPrompt))
  const stepValidations = Object.fromEntries(steps.map(step => [step.key, true]))
  const stepper = !isGroupedSizeFlow && steps.length > 1
    ? (
      <BubbleNavigation
        activeStep={navSteps[activeStepIndex] || navSteps[0]}
        interactive={false}
        stepValidations={stepValidations}
        steps={navSteps}
        variant="plain"
      />
    )
    : null

  useEffect(() => {
    setSelectVariantPrompts((prev: PromptSelectVariant[]) => {
      const all = prompts.every(isSelectVariantPrompt)
      if (!all) {
        return []
      }
      const addToCartPromptSelectVariants = prompts.filter(isSelectVariantPrompt)
      return prev.length < addToCartPromptSelectVariants.length ? addToCartPromptSelectVariants : prev
    })
  }, [prompts])

  const closeModal = useCallback((callback?: () => void) => {
    setSelectVariantPrompts([])
    if (modalActionsRef.current) {
      modalActionsRef.current?.close()
    }
    if (callback) {
      callback()
    }
  }, [modalActionsRef])

  if (!activePrompt) {
    return null
  }
  if (variantModalInAction.current && activePrompt.__typename === 'AddToCartPromptSuccess') {
    variantModalInAction.current = false
    modalActionsRef.current?.runReloadAnimation()
  }

  if (activePrompt.__typename === 'AddToCartPromptGiftcard') {
    if (activePrompt.code === 'giftcard_amount_missing' || activePrompt.code === 'giftcard_amount_out_of_bounds') {
      return (
        <GiftCardAmountDrawer
          defaultAmount={promptsOrigin?.[activePrompt.index]?.giftcard?.amount || undefined}
          loading={isLoading}
          onOpenChange={(open) => {
            if (!open) {
              onClose()
            }
          }}
          onSubmit={(amount) => {
            const input: AddToCartProductInput[] = cloneDeep(promptsOrigin)!
            input[activePrompt.index] = {
              ...input[activePrompt.index],
              giftcard: {
                ...input[activePrompt.index]?.giftcard,
                amount,
              },
            }
            onSubmit(input)
          }}
          open
        />
      )
    }

    return (
      <GiftCardTextDrawer
        loading={isLoading}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            onClose()
          }
        }}
        onSubmit={(message) => {
          const input: AddToCartProductInput[] = cloneDeep(promptsOrigin)!
          input[activePrompt.index] = {
            ...input[activePrompt.index],
            giftcard: {
              amount: input[activePrompt.index]?.giftcard?.amount || 0,
              text: message,
            },
          }
          onSubmit(input)
        }}
        open
      />
    )
  }

  if (
    activePrompt.__typename === 'AddToCartPromptPersonalization'
    || activePrompt.__typename === 'AddToCartPromptSelectVariant'
    || activePrompt.__typename === 'AddToCartPromptSuccess'
  ) {
    const isPersonalization = activePrompt.__typename === 'AddToCartPromptPersonalization'
    const personalizations = isPersonalization && filter(activePrompt.personalizations || [], ['masterVariant.type.value.key', selectedPersonalizationType])
    // @ts-ignore
    const activePersonalizations: ProductCrossSellAndPersonalizationContentFragment | null = isPersonalization && activePrompt.personalizations

    variantModalInAction.current = ['AddToCartPromptSelectVariant', 'AddToCartPromptPersonalization'].includes(activePrompt.__typename)

    if (!isPersonalization && activePrompt.__typename === 'AddToCartPromptSelectVariant' && activePrompt.product) {
      return (
        <SizeDrawer
          loading={isLoading}
          onOpenChange={(open) => {
            if (!open) {
              onClose()
            }
          }}
          onSubmit={(variantID) => {
            const input: AddToCartProductInput[] = cloneDeep(promptsOrigin)!
            input[activePrompt.index].variantID = variantID
            onSubmit(input, sourceID ? { sourceID } : undefined)
          }}
          open
          product={activePrompt.product}
          promptSelectVariants={selectVariantPrompts}
          title={stepper}
        />
      )
    }

    return (
      <NativeModal
        actionRef={modalActionsRef}
        alignment="right"
        backdrop
        className={styles.newDialog}
        closeOnBackDrop={enableBackdropClick}
        closeOnEscape={enableBackdropClick}
        defaultOpen
        onClose={onClose}
        portal={portal}
      >
        {activePrompt.__typename === 'AddToCartPromptSuccess' && (
          <SuccessPrompt
            onClose={() => {
              closeModal(onClose)
              setSourceIDPrompt(null)
            }}
            onModalToggle={(v) => {
              if (v) {
                setEnableBackdropClick(!v)
              } else {
                setTimeout(() => setEnableBackdropClick(!v), 100)
              }
            }}
            origins={promptsOrigin}
            primaryLineItemID={activePrompt.primaryLineItemID || undefined}
            tactics={activePrompt.tactics}
          />
        )}
        {isPersonalization && personalizations && (selectedPersonalizationType === 'deboss' || selectedPersonalizationType === 'engrave') && (
          <TextPersonalizationPrompt
            availablePersonalizations={personalizations}
            onSubmit={async (productID, personalization) => {
              const input: AddToCartProductInput[] = cloneDeep(promptsOrigin)!

              if (!personalization) {
                return onSubmit(removeAddToCartInput(input, activePrompt.index))
              }

              input[activePrompt.index].productID = productID!
              input[activePrompt.index].personalization = {
                ...input[activePrompt.index].personalization || {},
                text: personalization.text,
              }

              return onSubmit(input)
            }}
            selectedPersonalization={activePersonalizations || null}
            title={stepper}
            type={selectedPersonalizationType}
          />
        )}
      </NativeModal>
    )
  }

  if (activePrompt.__typename === 'AddToCartPromptError') {
    return (
      <ErrorPromptModal
        actionRef={modalActionsRef}
        code={activePrompt.code}
        defaultOpen
        message={activePrompt.message}
        onClose={() => closeModal(onClose)}
        portal={portal}
        title={'title' in activePrompt ? `${activePrompt.title}` : undefined}
      />
    )
  }

  return null
}
